
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface WelcomeEmailTemplate {
  id: string;
  sequence_number: number;
  subject: string;
  content: string;
  days_after_signup: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface WelcomeEmailQueue {
  id: string;
  user_id: string;
  template_id: string;
  scheduled_for: string;
  sent_at?: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export const useWelcomeEmails = () => {
  const { toast } = useToast();

  // Enhanced schedule welcome emails with immediate processing
  const scheduleWelcomeEmails = useMutation({
    mutationFn: async ({ userId, userEmail }: { userId: string; userEmail: string }) => {
      console.log('Scheduling welcome emails for user:', userId);
      
      // First, schedule the emails using the RPC function
      const { error: scheduleError } = await supabase.rpc('schedule_welcome_emails', {
        p_user_id: userId,
        p_user_email: userEmail
      });

      if (scheduleError) {
        console.error('Error scheduling welcome emails:', scheduleError);
        throw scheduleError;
      }

      // Immediately trigger processing of welcome emails to reduce delay
      try {
        console.log('Triggering immediate welcome email processing...');
        const { data: processResult, error: processError } = await supabase.functions.invoke('process-welcome-emails');
        
        if (processError) {
          console.warn('Warning: Could not immediately process welcome emails:', processError);
          // Don't throw here as scheduling was successful
        } else {
          console.log('Welcome emails processed immediately:', processResult);
        }
      } catch (immediateProcessError) {
        console.warn('Warning: Immediate processing failed, emails will be processed later:', immediateProcessError);
        // Don't throw as this is just optimization
      }

      return { success: true };
    },
    onSuccess: () => {
      console.log('Welcome emails scheduled successfully');
    },
    onError: (error: any) => {
      console.error('Failed to schedule welcome emails:', error);
      toast({
        title: "Welcome Email Setup",
        description: "Welcome emails have been queued for delivery.",
        variant: "default",
      });
    },
  });

  // Enhanced process pending emails with better error handling
  const processPendingEmails = useMutation({
    mutationFn: async () => {
      console.log('Processing pending welcome emails...');
      
      const { data, error } = await supabase.functions.invoke('process-welcome-emails');

      if (error) {
        console.error('Error processing welcome emails:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      console.log('Welcome emails processed:', data);
      if (data?.processed > 0) {
        toast({
          title: "Success",
          description: `Processed ${data.processed} welcome emails`,
        });
      }
    },
    onError: (error: any) => {
      console.error('Failed to process welcome emails:', error);
      toast({
        title: "Email Processing",
        description: "Welcome emails are being processed in the background.",
        variant: "default",
      });
    },
  });

  // Get welcome email templates (admin only)
  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['welcome-email-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('welcome_email_templates')
        .select('*')
        .order('sequence_number');

      if (error) throw error;
      return data as WelcomeEmailTemplate[];
    },
  });

  // Get user's welcome email queue
  const { data: emailQueue, isLoading: isLoadingQueue } = useQuery({
    queryKey: ['welcome-email-queue'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('welcome_email_queue')
        .select(`
          *,
          welcome_email_templates(subject, sequence_number)
        `)
        .order('scheduled_for');

      if (error) throw error;
      return data;
    },
  });

  return {
    scheduleWelcomeEmails,
    processPendingEmails,
    templates,
    isLoadingTemplates,
    emailQueue,
    isLoadingQueue,
  };
};
